import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ResponsiveBar} from "@nivo/bar";
import {useAtomValue} from "jotai";
import {BoardSearchResult, ChartTooltip} from "../../assets/GlobalStyles";
import Table from "../../components/table";
import HorizontalScrollGuide from "../../components/table/HorizontalScrollGuide";
import {ReportsCondition} from "../../components/reports/Condition";
import {sort} from "../../components/reports/sortList";
import {decimalFormat} from "../../common/StringUtils";
import {reportsStatics, reportsStaticsHourlyAllColumn, reportsUserStaticsHourlyAllColumn} from "./entity/period";
import {lockedRows, summaryReducer} from "./entity/common";
import {tokenResultAtom} from "../login/entity";
import {selectAdminStaticsHourAll, selectUserStaticsHourlyAll} from "../../services/reports/periodAxios";
import {UserInfo} from "../layout";
import {calculateEcpm, formatEcpm, metricColor} from "./entity/metrics";
import useInventoryOptions from "./hooks/useInventoryOptions";
import ReportMetricPanel from "../../components/reports/ReportMetricPanel";
import {
  CollapseIcon, ConditionBody, ConditionSummary, ExcelHeaderButton, HeaderRight,
  ReportCard, ReportPage, SectionAccent, SectionHeader, SectionHeading, SummaryChip, TableBody
} from "./ReportSharedLayout";

const formatRevenue = value => Number(value || 0).toLocaleString("ko-KR", {
  minimumFractionDigits:1, maximumFractionDigits:1
});

function HourChart({selectKeys, data, compactYAxis = false}) {
  const formatValue = (value, metric) => metric === "revenueAmount"
      ? formatRevenue(value)
      : metric === "ecpm" ? formatEcpm(value) : decimalFormat(value);
  return <ResponsiveBar
    data={data} keys={selectKeys} indexBy="historyHour" groupMode="grouped" height={340}
    margin={{top:30,right:30,bottom:70,left:64}} padding={0.4}
    valueScale={{type:"linear",min:0,max:"auto",nice:true}} indexScale={{type:"band",round:true}}
    colors={({id})=>metricColor(id)} enableLabel={false} enableGridY
    axisLeft={{tickSize:0,tickPadding:10,tickValues:compactYAxis?4:5,format:decimalFormat}}
    axisBottom={{tickSize:0,tickPadding:15,format:value=>`${String(value).padStart(2,"0")}시`}}
    tooltip={({id,indexValue,value}) => <ChartTooltip><p>{String(indexValue).padStart(2,"0")}시</p><p>{formatValue(value,id)}</p></ChartTooltip>}
    animate motionStiffness={120} motionDamping={15}
  />
}

export default function ReportsHourPeriod(){
  const [searchCondition,setSearchCondition] = useState(reportsStatics)
  const [searchState,setSearchState] = useState(reportsStatics)
  const [chartKeys,setChartKeys] = useState(["requestCount","exposureCount","revenueAmount","ecpm"])
  const [totalCount,setTotalCount] = useState(0)
  const [chartData,setChartData] = useState([])
  const [creativeInfo,setCreativeInfo] = useState({})
  const [isConditionOpen,setIsConditionOpen] = useState(false)
  const [downloadTrigger,setDownloadTrigger] = useState(0)
  const tokenInfoState = useAtomValue(tokenResultAtom)
  const userInfoState = useAtomValue(UserInfo)
  const {inventoryOptions,loadInventoryOptions}=useInventoryOptions({searchState,tokenInfoState,requireMedia:true})

  const summary = useMemo(() => chartData.reduce((sum,item)=>({
    requestCount:sum.requestCount+(Number(item.requestCount)||0),
    exposureCount:sum.exposureCount+(Number(item.exposureCount)||0),
    revenueAmount:sum.revenueAmount+(Number(item.revenueAmount)||0),
  }),{requestCount:0,exposureCount:0,revenueAmount:0}),[chartData])

  const requestData = useCallback(async condition => tokenInfoState.role === "NORMAL"
      ? selectUserStaticsHourlyAll(tokenInfoState.id,condition)
      : selectAdminStaticsHourAll(creativeInfo.id,condition),
    [creativeInfo.id,tokenInfoState.id,tokenInfoState.role])

  const handleSearchCondition = useCallback(async ({skip,limit,sortInfo}) => {
    const response = await requestData({
      ...searchCondition,pageSize:30,currentPage:skip/limit===0?1:skip/limit+1,
      sortType:sort("HOUR_DESC",sortInfo)
    })
    if(!response) return {data:[],count:0}
    setTotalCount(response.totalElements)
    return {data:response.content,count:response.totalElements}
  },[requestData,searchCondition])

  const fetchChart = useCallback(async () => {
    const response = await requestData({...searchCondition,pageSize:24,currentPage:1,sortType:"HOUR_ASC"})
    const data = (response?.content || []).map(item => ({
      ...item, ecpm:calculateEcpm(item.revenueAmount,item.exposureCount)
    }))
    setChartData(data)
  },[requestData,searchCondition])
  useEffect(()=>{fetchChart()},[fetchChart])

  const onSearch = ({mediaInfo}={}) => {
    setCreativeInfo(mediaInfo || {})
    setSearchCondition({...searchState})
  }
  const selectedInventoryNames = useMemo(() => {
    const selectedIds = searchCondition.inventoryId
        ? String(searchCondition.inventoryId).split(",").filter(Boolean)
        : []
    if(!selectedIds.length) return "전체"
    const names = inventoryOptions
        .filter(option => selectedIds.includes(String(option.value)))
        .map(option => option.label)
    return names.length === 1 ? names[0] : `${names.length || selectedIds.length}개 지면`
  },[inventoryOptions,searchCondition.inventoryId])
  const toggleCondition = () => setIsConditionOpen(open=>!open)

  return <ReportPage>
    <ReportCard $condition>
      <SectionHeader $clickable $open={isConditionOpen} role="button" tabIndex={0}
        aria-expanded={isConditionOpen} onClick={toggleCondition}
        onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();toggleCondition()}}}>
        <SectionHeading><SectionAccent/><div><strong>조회 조건</strong><span>매체, 광고 상품, 디바이스와 기간을 기준으로 시간대별 데이터를 조회합니다.</span></div></SectionHeading>
        <HeaderRight>
          {!isConditionOpen && <ConditionSummary>
            <SummaryChip>매체 <b>{creativeInfo.siteName || "전체"}</b></SummaryChip>
            <SummaryChip>지면 <b>{selectedInventoryNames}</b></SummaryChip>
            <SummaryChip>상품 <b>{searchCondition.productType || "전체"}</b></SummaryChip>
            <SummaryChip>디바이스 <b>{searchCondition.deviceType || "전체"}</b></SummaryChip>
            <SummaryChip>기간 <b>{searchCondition.searchStartDate} ~ {searchCondition.searchEndDate}</b></SummaryChip>
          </ConditionSummary>}
          <CollapseIcon $open={isConditionOpen}/>
        </HeaderRight>
      </SectionHeader>
      {isConditionOpen && <ConditionBody onClick={e=>e.stopPropagation()}>
        <ReportsCondition modern searchMediaInfo={creativeInfo} searchState={searchState}
          setSearchState={setSearchState} onSearch={onSearch}
          showInventory={tokenInfoState.role !== "NORMAL"} inventoryMulti
          inventoryOptions={inventoryOptions} inventoryRequiresMedia
          onMediaDraftChange={loadInventoryOptions}/>
      </ConditionBody>}
    </ReportCard>

    <ReportCard>
      <ReportMetricPanel summary={summary} chartKeys={chartKeys} onChartKeyChange={setChartKeys}
          description="확인할 지표를 여러 개 선택하면 시간대별 차트에 함께 반영됩니다." showEcpm>
        <HourChart selectKeys={chartKeys} data={chartData} compactYAxis={tokenInfoState.role === "NORMAL"}/>
      </ReportMetricPanel>
    </ReportCard>

    <ReportCard>
      <SectionHeader>
        <SectionHeading><SectionAccent/><div><strong>시간대별 데이터</strong><span>조회 조건에 해당하는 시간대별 상세 성과를 확인합니다.</span></div></SectionHeading>
        <ExcelHeaderButton type="button" onClick={()=>setDownloadTrigger(v=>v+1)}>엑셀 저장</ExcelHeaderButton>
      </SectionHeader>
      <TableBody><HorizontalScrollGuide/><BoardSearchResult>
        <Table columns={userInfoState.email === "" ? reportsStaticsHourlyAllColumn : reportsUserStaticsHourlyAllColumn}
          lockedRows={lockedRows} summaryReducer={summaryReducer} totalCount={[totalCount,"보고서"]}
          data={handleSearchCondition} defaultSortInfo={{name:"historyHour",dir:-1}}
          pagination livePagination downloadList={false} downloadTrigger={downloadTrigger}
          fileName="시간대별_보고서" scrollThreshold={0.7} headerHeight={44} style={{minHeight:500}}/>
      </BoardSearchResult></TableBody>
    </ReportCard>
  </ReportPage>
}
